import { useState } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import {
  proposeVideoConsultation,
  confirmVideoConsultation,
  cancelVideoConsultation,
} from '../services/api';
import { useConfirm, type ConfirmOptions } from '../components/ConfirmProvider';
import { useToast } from '../components/ToastProvider';
import { BusyButton } from '../components/BusyButton';
import { errMessage } from '../errors';
import { personName } from '../people';

type Case = components['schemas']['Case'];

/**
 * Video consultation workflow (Option A: reviewer proposes → local doctor confirms).
 * - Reviewer/admin: propose a date/time (picked in their local zone, sent as UTC) while the case
 *   is ASSIGNED/UNDER_REVIEW, or re-propose while PROPOSED.
 * - Local doctor/admin: confirm the proposed time (creates the Zoom meeting) or cancel.
 * - Once SCHEDULED, the join link + time show here; the doctor coordinates the patient offline.
 * Times are stored UTC and rendered in the viewer's local zone (labelled).
 */

/**
 * Format a UTC instant in the viewer's local zone, with the zone abbreviation. Uses explicit
 * component options — combining the dateStyle/timeStyle shortcuts with timeZoneName is invalid
 * and throws in some engines, which is what previously blanked the page.
 */
function fmtLocal(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function CaseVideoConsultation({
  theCase,
  roles,
  onChanged,
}: {
  theCase: Case;
  roles: string[];
  onChanged: () => void;
}) {
  const vc = theCase.videoConsultation;
  const isAdmin = roles.includes('ADMIN') || roles.includes('GLOBAL_ADMIN');
  const isReviewer = roles.includes('REVIEWER') || isAdmin;
  const isLocal = roles.includes('LOCAL_DOCTOR') || isAdmin;

  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  // Which action is running, so only the clicked button spins (they share one handler).
  const [runningAction, setRunningAction] = useState<'confirm' | 'propose' | 'cancel' | null>(null);
  const busy = runningAction !== null;
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const activeProposed = vc?.status === 'PROPOSED';
  const activeScheduled = vc?.status === 'SCHEDULED';
  const active = activeProposed || activeScheduled;

  // Reviewer can propose from ASSIGNED/UNDER_REVIEW, or re-propose while PROPOSED.
  const canPropose =
    isReviewer && (['ASSIGNED', 'UNDER_REVIEW'].includes(theCase.status) || activeProposed);
  const canConfirm = isLocal && activeProposed;
  const canCancel = active && (isReviewer || isLocal);

  // Don't render the card at all if there's nothing to show and no action available.
  if (!vc && !canPropose) return null;

  const run = async (
    action: 'confirm' | 'propose' | 'cancel',
    fn: () => Promise<unknown>,
    opts?: { confirm?: ConfirmOptions; success?: string },
  ) => {
    if (opts?.confirm && !(await confirm(opts.confirm))) return;
    setRunningAction(action);
    setError(null);
    try {
      await fn();
      if (opts?.success) toast.success(opts.success);
      onChanged();
    } catch (e) {
      const msg = errMessage(e, 'Action failed');
      setError(msg);
      toast.error(msg);
    } finally {
      setRunningAction(null);
    }
  };

  const statusVariant: Record<string, string> = {
    PROPOSED: 'info',
    SCHEDULED: 'success',
    COMPLETED: 'secondary',
    CANCELLED: 'secondary',
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Video consultation</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {vc && (
          <div className="mb-3">
            <div className="mb-1">
              <Badge bg={statusVariant[vc.status] ?? 'secondary'}>{vc.status}</Badge>
            </div>
            <div className="small text-muted">
              {activeScheduled ? 'Scheduled for' : 'Proposed for'}:{' '}
              <strong>{fmtLocal(activeScheduled ? vc.scheduledAt : vc.proposedAt)}</strong>{' '}
              <span className="text-muted">(your local time)</span>
            </div>
            <div className="small text-muted">
              Proposed by {personName(vc.proposedByName, vc.proposedBy)}
              {vc.confirmedBy && <> · confirmed by {personName(vc.confirmedByName, vc.confirmedBy)}</>}
            </div>
            {vc.note && <div className="small mt-1">Note: {vc.note}</div>}
            {activeScheduled && (
              <div className="mt-3">
                {/* HOST link — only present in the response for the host (reviewer/admin). It
                    starts the meeting as host; must NOT be shared with the patient. */}
                {vc.startUrl && (
                  <div className="mb-3 p-2 rounded border border-warning-subtle bg-warning-subtle">
                    <div className="fw-semibold small mb-1">Host link — starts the call</div>
                    <a
                      className="btn btn-sm btn-warning"
                      href={vc.startUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Start consultation (as host)
                    </a>
                    <div className="small text-muted mt-1">
                      The reviewer (host) uses this to start the meeting. It is a private host
                      link — do <strong>not</strong> share it with the patient. The patient uses the
                      separate join link below.
                    </div>
                  </div>
                )}

                {/* PARTICIPANT link — safe to share with the patient. */}
                {vc.joinUrl && (
                  <div className="p-2 rounded border">
                    <div className="fw-semibold small mb-1">Patient join link</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <a
                        className="btn btn-sm btn-primary"
                        href={vc.joinUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {vc.startUrl ? 'Open patient link' : 'Join meeting'}
                      </a>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          void navigator.clipboard?.writeText(vc.joinUrl ?? '');
                          toast.success('Patient join link copied.');
                        }}
                      >
                        Copy patient link
                      </Button>
                    </div>
                    <div className="small text-muted mt-1">
                      Share <strong>this</strong> link and the time with the patient (e.g. via
                      WhatsApp/phone). {vc.startUrl ? '' : 'The reviewer starts the call as host.'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {canConfirm && (
          <div className="d-flex gap-2 mb-3">
            <BusyButton
              variant="success"
              size="sm"
              loading={runningAction === 'confirm'}
              busyLabel="Scheduling…"
              disabled={busy}
              onClick={() =>
                run('confirm', () => confirmVideoConsultation(theCase.caseId, { version: theCase.version }), {
                  success: 'Video consultation scheduled — join link is now available.',
                })
              }
            >
              Confirm &amp; schedule meeting
            </BusyButton>
          </div>
        )}

        {canPropose && (
          <div className="border-top pt-3">
            <h3 className="h6">{activeProposed ? 'Propose a different time' : 'Propose a video consultation'}</h3>
            <p className="small text-muted mb-2">
              Pick a time in your local zone. The local doctor confirms it with the patient; the
              meeting link is generated on confirmation.
            </p>
            <Form.Group className="mb-2">
              <Form.Label className="small">Date &amp; time (your local time)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small">Note (optional, non-clinical)</Form.Label>
              <Form.Control value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </Form.Group>
            <BusyButton
              variant="outline-primary"
              size="sm"
              loading={runningAction === 'propose'}
              busyLabel="Proposing…"
              disabled={busy || !when}
              onClick={() =>
                run(
                  'propose',
                  () =>
                    proposeVideoConsultation(theCase.caseId, {
                      version: theCase.version,
                      // datetime-local has no zone; convert the local wall-time to a UTC instant.
                      scheduledAt: new Date(when).toISOString(),
                      note: note.trim() || null,
                    }),
                  { success: 'Time proposed — the local doctor will confirm.' },
                )
              }
            >
              Propose time
            </BusyButton>
          </div>
        )}

        {canCancel && (
          <div className="mt-2">
            <BusyButton
              variant="link"
              size="sm"
              className="text-danger p-0"
              loading={runningAction === 'cancel'}
              busyLabel="Cancelling…"
              disabled={busy}
              onClick={() =>
                run(
                  'cancel',
                  () => cancelVideoConsultation(theCase.caseId, { version: theCase.version }),
                  {
                    confirm: {
                      title: 'Cancel video consultation?',
                      body: 'The proposed/scheduled consultation will be cancelled and the case returns to review. Continue?',
                      confirmLabel: 'Cancel consultation',
                      cancelLabel: 'Keep it',
                      variant: 'danger',
                    },
                    success: 'Video consultation cancelled.',
                  },
                )
              }
            >
              Cancel video consultation
            </BusyButton>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
